import { useTranslation } from 'react-i18next';
import { Skeleton } from '@/components/ui-kit/skeleton';
import { APP_PATHS } from '@/constant/app-paths';
import { HRM_PERMISSIONS } from '@/constant/hr/permissions';
import { useHasPermission } from '@/hooks/use-has-permission';
import { useGetOvertimeBalance } from '@/modules/time-attendance/overtime/hooks/use-overtime';
import {
  useGetLeaveSheet,
  useLeaveSheetPolicy,
} from '@/modules/time-off/team-time-off/hooks/use-leave-sheet';
import { isOvertimeLeave } from '@/modules/time-off/team-time-off/utils/overtime-leave';
import { HomeCard } from '../home-card/home-card';

/** The leave types Balances shows (agreed 2 Nov 2026), matched on their titles; any other is left out. */
const HOME_BALANCE_KINDS = [/annual/i, /casual/i, /sick/i, /overtime/i];

/** Days as they read: whole numbers as they are, halves with one decimal. */
const days = (value: number) => (Number.isInteger(value) ? String(value) : value.toFixed(1));

/** Signed "+h:mm" of a number of seconds (overtime can be owed). */
const signedHours = (seconds: number) => {
  const minutes = Math.round(Math.abs(seconds) / 60);
  const text = `${Math.floor(minutes / 60)}:${String(minutes % 60).padStart(2, '0')}`;
  return `${seconds < 0 ? '−' : '+'}${text}`;
};

const R = 20;
const CIRCUMFERENCE = 2 * Math.PI * R;

/**
 * A 52px ring of the days left out of the allotment (prototype `homeRing`: `--primary` on `--muted`; 56px
 * there, a little smaller here so four stacked balances fit Home without scrolling).
 */
const BalanceRing = ({ left, total }: { left: number; total: number }) => {
  const share = total > 0 ? Math.max(0, Math.min(1, left / total)) : 0;
  return (
    <svg viewBox="0 0 52 52" className="size-[52px] flex-none" aria-hidden>
      <circle cx="26" cy="26" r={R} fill="none" strokeWidth={6} stroke="oklch(var(--muted))" />
      <circle
        cx="26"
        cy="26"
        r={R}
        fill="none"
        strokeWidth={6}
        stroke="oklch(var(--primary))"
        strokeLinecap="round"
        strokeDasharray={`${(CIRCUMFERENCE * share).toFixed(1)} ${CIRCUMFERENCE.toFixed(1)}`}
        transform="rotate(-90 26 26)"
      />
      <text
        x="26"
        y="30"
        textAnchor="middle"
        className="fill-foreground text-sm font-semibold tabular-nums"
      >
        {days(left)}
      </text>
    </svg>
  );
};

/**
 * Balances (HR Module Prototype › Home, `homeBalances`): a row for each of Annual, Casual, Sick and
 * Overtime leave the current HR term assigns (no other type), a ring of its days left beside "<type> /
 * N of M days left" (Overtime Leave: the overtime balance in days), then a two-cell box of
 * TOIL days and overtime hours. Requesting time off and TOIL is on My Time Off only (2 Nov 2026).
 */
export const BalancesCard = ({ userId }: { userId: string }) => {
  const { t } = useTranslation();
  const { policy } = useLeaveSheetPolicy(userId);
  const { data: sheet, isLoading } = useGetLeaveSheet(userId, undefined, policy);
  // Overtime only for those who earn it (as on My Attendance).
  const hasOvertime = useHasPermission(HRM_PERMISSIONS.PEOPLE.OVERTIME_ENABLED);
  // The balance also pays an assigned Overtime Leave.
  const hasOvertimeLeave = (sheet?.categories ?? []).some((category) =>
    isOvertimeLeave(category.LeaveCategory)
  );
  const { data: overtime } = useGetOvertimeBalance(hasOvertime || hasOvertimeLeave ? userId : '');

  // Only the four everyday balances, in this order, and only those the term assigns (2 Nov 2026).
  const categories = HOME_BALANCE_KINDS.map((kind) =>
    (sheet?.categories ?? []).find((category) => kind.test(category.LeaveCategory))
  )
    .filter((category): category is NonNullable<typeof category> => Boolean(category))
    .map((category) =>
      // Overtime Leave has no allotment: it is paid from the overtime balance, in days.
      isOvertimeLeave(category.LeaveCategory)
        ? {
            ...category,
            Remaining: overtime?.TotalRemainingOvertimeInDays ?? 0,
            Allocated: Math.max(overtime?.TotalRemainingOvertimeInDays ?? 0, 0),
            IsOvertime: true,
          }
        : { ...category, IsOvertime: false }
    )
    .filter((category) => category.IsOvertime || !category.IsUnlimited);
  const toilLeft = sheet?.summary && sheet.policy.toil ? sheet.summary.toilLeft : null;
  const overtimeSeconds = hasOvertime ? (overtime?.TotalRemainingOvertimeHourInSecond ?? 0) : null;

  return (
    <HomeCard title={t('HOME_BALANCES')} viewTo={APP_PATHS.MY_TIME_OFF}>
      {isLoading || !policy ? (
        <Skeleton className="h-32 w-full" />
      ) : (
        <div className="flex flex-col gap-3">
          {categories.length ? (
            // One balance a row (user, 3 Nov 2026), kept compact so four of them don't make this card (and its
            // grid row) taller than the rest of Home on a desktop screen.
            <div className="grid gap-2.5">
              {categories.map((category) => (
                <div key={category.LeaveCategoryId} className="flex min-w-0 items-center gap-3">
                  <BalanceRing left={category.Remaining} total={category.Allocated} />
                  <div className="grid min-w-0">
                    <b className="truncate font-medium">{category.LeaveCategory}</b>
                    <span className="text-[13px] text-muted-foreground">
                      {category.IsOvertime
                        ? t('HOME_DAYS_AVAILABLE', { left: days(category.Remaining) })
                        : t('HOME_DAYS_LEFT', {
                            left: days(category.Remaining),
                            total: days(category.Allocated),
                          })}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">{t('NO_CURRENT_HR_TERM')}</p>
          )}
          {(toilLeft !== null || overtimeSeconds !== null) && (
            <div className="grid grid-cols-2 rounded-md border">
              <span className="grid gap-0.5 px-3 py-2">
                <span className="text-xs text-muted-foreground">{t('TOIL')}</span>
                <b className="font-semibold tabular-nums">
                  {toilLeft !== null ? t('HOME_N_DAYS', { days: days(toilLeft) }) : '—'}
                </b>
              </span>
              <span className="grid gap-0.5 border-l px-3 py-2">
                <span className="text-xs text-muted-foreground">{t('HOME_OVERTIME_HOURS')}</span>
                <b className="font-semibold tabular-nums">
                  {overtimeSeconds !== null ? signedHours(overtimeSeconds) : '—'}
                </b>
              </span>
            </div>
          )}
        </div>
      )}
    </HomeCard>
  );
};
